import { forwardRef, useId, type SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  options: SelectOption[];
  error?: string;
  /** Text for the empty leading option (no selection). */
  placeholder?: string;
  /**
   * Shadows the native numeric `size` attribute (which is omitted above) - a
   * visual scale is what every call site actually wants here, and the native
   * row-count attribute has no meaning for a closed single-select.
   *
   * `md` (default) = the standard 16px-radius form field.
   * `sm` = compact inline control for toolbars and table footers (e.g. the
   * rows-per-page picker), which the form-field box model is too tall for.
   */
  size?: 'sm' | 'md';
}

// Each size owns its full box model (height, padding, radius, text size) rather
// than callers patching a too-tall field with className overrides - which is how
// the rows-per-page picker ended up clipping its own value.
const SIZE_CLASSES: Record<NonNullable<SelectProps['size']>, string> = {
  sm: 'h-8 rounded-lg py-0 pl-3 pr-8 text-sm',
  md: 'rounded-input py-2 pl-4 pr-10 text-input',
};

const CARET_POSITION: Record<NonNullable<SelectProps['size']>, string> = {
  sm: 'right-2.5',
  md: 'right-4',
};

// The disabled and placeholder tones stay on their translucent brand values in
// light (they have no flat-token equivalent) and get an `admin-dark:` override,
// which emits no rule in light mode. See the variant's note in index.css.
const SELECT_SURFACE =
  'border-text-muted bg-surface-card text-text-primary disabled:border-teal/5 disabled:bg-teal/5 ' +
  'admin-dark:border-border-strong admin-dark:disabled:border-border-subtle admin-dark:disabled:bg-surface-raised';

/** Small filled caret (Figma 287:3098) - native select arrows differ per OS. */
function CaretIcon({ size }: { size: NonNullable<SelectProps['size']> }) {
  return (
    <svg
      width="10"
      height="6"
      viewBox="0 0 10 6"
      aria-hidden="true"
      className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-text-secondary ${CARET_POSITION[size]}`}
    >
      <path d="M0 0h10L5 6z" fill="currentColor" />
    </svg>
  );
}

/**
 * Design-system select primitive (styled native `<select>`). Native keeps it
 * accessible for a simple single-choice list; complex comboboxes would use a
 * Radix primitive instead (mnr-standards UI component architecture).
 * While the empty placeholder option is selected the text renders in the
 * muted teal placeholder tone (Figma 287:3098).
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, error, placeholder, size = 'md', className = '', ...props },
  ref,
) {
  const errorId = useId();
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <select
          ref={ref}
          aria-label={label}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
          className={`w-full appearance-none border focus:border-green focus:outline-none disabled:cursor-not-allowed has-[option[value='']:checked]:text-teal/70 admin-dark:has-[option[value='']:checked]:text-text-muted ${SIZE_CLASSES[size]} ${SELECT_SURFACE} ${className}`.trim()}
          {...props}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value} className="text-navy">
              {option.label}
            </option>
          ))}
        </select>
        <CaretIcon size={size} />
      </div>
      {hasError && (
        <p id={errorId} role="alert" className="pl-1 text-xs text-red">
          {error}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';
